import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useDeleteCoManufacturingOrderMutation,
  useSubmitCoManufacturingOrderMutation,
} from "../../../../store/apiSlice/manufacturingOrderApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

const extractErrorMessage = (error, defaultMsg) => {
  const data = error?.data;
  if (!data) return defaultMsg;
  if (data?.detail) return data.detail;
  if (Array.isArray(data?.status) && data.status.length > 0) {
    return data.status[0];
  }
  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    const val = data[firstKey];
    if (Array.isArray(val) && val.length > 0) {
      return val[0];
    }
    if (typeof val === "string") return val;
  }
  return defaultMsg;
};

const CoManufacturingOrderAction = ({
  selectedOrderId,
  setSelectedOrder,
  selectedOrderFromListview = [],
}) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [submitMO, { isLoading: isSubmittingMO }] = useSubmitCoManufacturingOrderMutation();
  const [cancelMO, { isLoading: isCancellingMO }] = useDeleteCoManufacturingOrderMutation();

  const [isCancelling, setIsCancelling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateClick = () => {
    navigate("/co-manufacturing-order/create");
  };

  const selectedOrder = selectedOrderFromListview?.[0];
  const isSingleSelected = selectedOrderId?.length === 1;

  const isDraftSelected =
    selectedOrder?.status?.id === "draft" || selectedOrder?.status === "draft";

  const handleCancelSubmit = async () => {
    try {
      await cancelMO(selectedOrderId[0]).unwrap();
      notify({
        type: "success",
        message: `Co-Manufacturing Order “${selectedOrder?.name || selectedOrderId[0]}” cancelled successfully.`,
      });
      setSelectedOrder([]);
      setIsCancelling(false);
    } catch (error) {
      notify({
        type: "error",
        message: extractErrorMessage(error, "Failed to cancel co-manufacturing order."),
      });
    }
  };

  const handleSubmitOrder = async () => {
    try {
      await submitMO(selectedOrderId[0]).unwrap();
      notify({
        type: "success",
        message: `Co-Manufacturing Order “${selectedOrder?.name || selectedOrderId[0]}” submitted successfully.`,
      });
      setSelectedOrder([]);
      setIsSubmitting(false);
    } catch (error) {
      notify({
        type: "error",
        message: extractErrorMessage(error, "Failed to submit co-manufacturing order."),
      });
    }
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "cancel",
        label: "Cancel",
        onClick: () => setIsCancelling(true),
        disabled: !isSingleSelected || !isDraftSelected || isCancellingMO,
        title:
          !isSingleSelected || !isDraftSelected
            ? "Select a single draft co-manufacturing order to cancel."
            : "",
        isLoading: isCancellingMO,
        isDanger: true,
      },
    ],
    [isSingleSelected, isDraftSelected, isCancellingMO],
  );

  return (
    <>
      <li className="tableButton activeState">
        <button onClick={handleCreateClick}>Create</button>
      </li>
      <li
        className={`tableButton ${isSingleSelected && isDraftSelected && !isSubmittingMO ? "activeState" : "disableState"}`}
      >
        <button
          onClick={() => setIsSubmitting(true)}
          disabled={!isSingleSelected || !isDraftSelected || isSubmittingMO}
          title={
            !isSingleSelected || !isDraftSelected
              ? "Select a single draft co-manufacturing order to submit."
              : ""
          }
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isSubmittingMO && <ButtonSpinner aria-hidden="true" />}
          {isSubmittingMO ? "Submitting…" : "Submit"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCancelling && (
        <ConfirmationModal
          titleOfModal="Cancel Co-Manufacturing Order"
          btnName="Cancel Order"
          closeModal={() => {
            setIsCancelling(false);
          }}
          handleModalWork={handleCancelSubmit}
          isLoading={isCancellingMO}
          description={
            <>
              Are you sure you want to cancel the co-manufacturing order{" "}
              <strong>{selectedOrder?.name || selectedOrderId[0]}</strong>?
            </>
          }
        />
      )}

      {isSubmitting && (
        <ConfirmationModal
          titleOfModal="Submit Co-Manufacturing Order"
          btnName="Submit Order"
          closeModal={() => {
            setIsSubmitting(false);
          }}
          handleModalWork={handleSubmitOrder}
          isLoading={isSubmittingMO}
          description={
            <>
              Are you sure you want to submit the co-manufacturing order{" "}
              <strong>{selectedOrder?.name || selectedOrderId[0]}</strong>?
            </>
          }
        />
      )}
    </>
  );
};

export const CoManufacturingOrderActionWithCheck = componentWithCondition(
  CoManufacturingOrderAction,
);
